import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, Package, CalendarClock, Info, Wallet, Trophy } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";

const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const fmtAxis = (v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}jt` : v >= 1000 ? `${Math.round(v / 1000)}rb` : v;
const fmtDate = (d) => new Date(d + "T00:00:00+07:00").toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Jakarta" });

function DailyTable({ series }) {
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const rows = [...series].reverse();

  const toggle = async (d) => {
    if (open === d) { setOpen(null); setDetail(null); return; }
    setOpen(d); setDetail(null);
    try {
      const { data } = await api.get(`/reports/omset/day?date=${d}`);
      setDetail(data);
    } catch { setDetail({ error: true }); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h2 className="font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Rincian Omset Harian</h2>
        <p className="text-xs text-slate-500">Klik tanggal untuk melihat resi terjual & pengeluaran hari itu.</p>
      </div>
      <table data-testid="omset-daily-table" className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-2 font-bold">Tanggal</th>
            <th className="text-right px-4 py-2 font-bold">Omset</th>
            <th className="text-right px-4 py-2 font-bold">Biaya</th>
            <th className="text-right px-4 py-2 font-bold">Profit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <React.Fragment key={s.date}>
              <tr data-testid={`omset-day-row-${s.date}`} onClick={() => toggle(s.date)} className={`border-t border-slate-100 cursor-pointer transition-colors ${open === s.date ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                <td className="px-4 py-2.5 font-semibold text-slate-900">{fmtDate(s.date)}</td>
                <td className="px-4 py-2.5 text-right text-slate-700" style={{fontFamily:'IBM Plex Sans'}}>{fmt(s.omset)}</td>
                <td className="px-4 py-2.5 text-right text-red-600" style={{fontFamily:'IBM Plex Sans'}}>{s.pengeluaran ? `-${fmt(s.pengeluaran)}` : "-"}</td>
                <td className={`px-4 py-2.5 text-right font-bold ${s.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} style={{fontFamily:'IBM Plex Sans'}}>{fmt(s.profit)}</td>
              </tr>
              {open === s.date && (
                <tr>
                  <td colSpan={4} className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                    <div data-testid={`omset-day-detail-${s.date}`} className="space-y-2">
                      {detail === null && <p className="text-xs text-slate-500">Memuat detail...</p>}
                      {detail?.error && <p className="text-xs text-red-600">Gagal memuat detail.</p>}
                      {detail && !detail.error && (
                        <>
                          {detail.sales.length === 0 && detail.expenses.length === 0 && (
                            <p className="text-xs text-slate-500">Tidak ada penjualan maupun pengeluaran pada hari ini.</p>
                          )}
                          {detail.sales.map((sale, i) => (
                            <div key={i} className="bg-white rounded-lg border border-slate-200 p-3">
                              <div className="flex justify-between gap-2 text-xs text-slate-500">
                                <span className="font-bold text-slate-700">{sale.courier}{sale.order_ref ? ` · ${sale.order_ref}` : ""}</span>
                                <span>{new Date(sale.time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })} · {sale.by}</span>
                              </div>
                              <div className="mt-1.5 space-y-0.5">
                                {sale.items.map((it, j) => (
                                  <div key={j} className="flex justify-between text-xs">
                                    <span className="text-slate-700 truncate">{it.name} × {it.qty}</span>
                                    <span className="text-slate-900 font-semibold flex-shrink-0" style={{fontFamily:'IBM Plex Sans'}}>{fmt(it.subtotal)}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex justify-between text-xs font-bold">
                                <span className="text-slate-600">Subtotal</span>
                                <span className="text-slate-900" style={{fontFamily:'IBM Plex Sans'}}>{fmt(sale.subtotal)}</span>
                              </div>
                            </div>
                          ))}
                          {detail.expenses.map((x) => (
                            <div key={x.id} className="flex justify-between gap-2 bg-red-50 rounded-lg border border-red-100 p-3 text-xs">
                              <span className="text-slate-700 truncate"><b className="capitalize">{x.category}</b> — {x.description || "pengeluaran"}</span>
                              <span className="text-red-600 font-bold flex-shrink-0" style={{fontFamily:'IBM Plex Sans'}}>-{fmt(x.amount)}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Omset() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/reports/omset?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  const series = (data?.series || []).map((s) => ({ ...s, label: s.date.slice(5).split("-").reverse().join("/") }));

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Keuangan</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Omset Masuk</h1>
        <p className="text-sm text-slate-600 mt-1">Dihitung otomatis dari produk terjual (resi terkonfirmasi) × harga jual masing-masing produk.</p>
      </div>

      <div className="flex gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`omset-${d}d`} onClick={() => setDays(d)} className={`h-10 px-4 rounded-full text-sm font-bold transition-colors ${days === d ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
            {d} Hari
          </button>
        ))}
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div data-testid="omset-total" className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Omset</p>
                <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><TrendingUp size={18} /></div>
              </div>
              <p className="mt-2 text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{fmt(data.total_omset)}</p>
              <p className="text-xs text-slate-500">{days} hari terakhir</p>
            </div>
            <div data-testid="omset-qty" className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Unit Terjual</p>
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Package size={18} /></div>
              </div>
              <p className="mt-2 text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{Number(data.total_qty).toLocaleString('id-ID')}</p>
              <p className="text-xs text-slate-500">unit terjual (resi terkonfirmasi)</p>
            </div>
            <div data-testid="omset-avg" className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Rata-rata / Hari</p>
                <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center"><CalendarClock size={18} /></div>
              </div>
              <p className="mt-2 text-2xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{fmt(Math.round(data.avg_per_day))}</p>
              <p className="text-xs text-slate-500">omset harian</p>
            </div>
            <div data-testid="omset-expense" className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pengeluaran</p>
                <div className="w-9 h-9 rounded-lg bg-red-100 text-red-700 flex items-center justify-center"><Wallet size={18} /></div>
              </div>
              <p className="mt-2 text-2xl font-extrabold text-red-600" style={{fontFamily:'Manrope'}}>-{fmt(data.total_pengeluaran)}</p>
              <p className="text-xs text-slate-500">gaji, pajak, operasional, dll</p>
            </div>
            <div data-testid="omset-profit" className={`rounded-xl border-2 p-4 col-span-2 sm:col-span-1 ${data.profit >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Profit Bersih</p>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${data.profit >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}><Trophy size={18} /></div>
              </div>
              <p className={`mt-2 text-2xl font-extrabold ${data.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} style={{fontFamily:'Manrope'}}>{fmt(data.profit)}</p>
              <p className="text-xs text-slate-500">omset − pengeluaran</p>
            </div>
          </div>

          {(data.expense_by_category || []).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Rincian Pengeluaran</h2>
                <Link to="/pengeluaran" data-testid="goto-pengeluaran" className="text-sm text-blue-600 font-semibold">Kelola →</Link>
              </div>
              <div className="space-y-2">
                {data.expense_by_category.map((c) => (
                  <div key={c.category} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 capitalize">{c.category}</span>
                    <span className="font-bold text-red-600" style={{fontFamily:'IBM Plex Sans'}}>-{fmt(c.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.total_omset === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-2 text-sm text-amber-800">
              <Info size={16} className="flex-shrink-0 mt-0.5" />
              <span>Omset masih Rp 0 — konfirmasi penjualan resi terlebih dahulu, dan pastikan <b>harga jual</b> produk sudah diisi di menu Produk.</span>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="font-bold text-slate-900 mb-4" style={{fontFamily:'Manrope'}}>Tren Omset Harian</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={fmtAxis} width={48} />
                  <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => `Tanggal ${l}`} />
                  <Line type="monotone" dataKey="omset" stroke="#2563EB" strokeWidth={2.5} dot={false} name="Omset" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <DailyTable series={data.series || []} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h2 className="font-bold text-slate-900 mb-4" style={{fontFamily:'Manrope'}}>Omset per Cabang</h2>
              {data.per_branch.length === 0 ? (
                <p className="text-sm text-slate-500">Belum ada pengiriman ke cabang pada periode ini.</p>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.per_branch} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={fmtAxis} width={48} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Bar dataKey="omset" fill="#2563EB" radius={[6, 6, 0, 0]} name="Omset" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <h2 className="font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Omset per Produk</h2>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Produk</th>
                    <th className="text-right px-4 py-2 font-bold">Qty</th>
                    <th className="text-right px-4 py-2 font-bold">Omset</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_product.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500">Belum ada data.</td></tr>
                  )}
                  {data.per_product.map((p) => (
                    <tr key={p.product_id} className="border-t border-slate-100">
                      <td className="px-4 py-2.5 font-semibold text-slate-900">{p.name}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600" style={{fontFamily:'IBM Plex Sans'}}>{p.qty}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-slate-900" style={{fontFamily:'IBM Plex Sans'}}>{fmt(p.omset)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
